import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { View, Text, ActivityIndicator, Linking } from './utils/webComponents';
import referralService from './services/referralService';
import { Analytics } from '@vercel/analytics/react';
// Context
import { FlashcardProvider } from './context/FlashcardContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Screens
import HomeScreen from './screens/HomeScreen.jsx';
import ClassDetailScreen from './screens/ClassDetailScreen.jsx';
import SetDetailScreen from './screens/SetDetailScreen.jsx';
import CreateSetScreen from './screens/CreateSetScreen.jsx';
import EditSetScreen from './screens/EditSetScreen.jsx';
import CreateFlashcardScreen from './screens/CreateFlashcardScreen.jsx';
import EditFlashcardScreen from './screens/EditFlashcardScreen.jsx';
import PlayScreen from './screens/PlayScreen.jsx';
import AIGenerateScreen from './screens/AIGenerateScreen.jsx';
import DocumentToFlashcardsScreen from './screens/DocumentToFlashcardsScreen.jsx';
import ProfileScreen from './screens/ProfileScreen.jsx';
import SettingsScreen from './screens/SettingsScreen.jsx';
import PricingScreen from './screens/PricingScreen.jsx';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen.jsx';
import TermsOfServiceScreen from './screens/TermsOfServiceScreen.jsx';
import HelpCenterScreen from './screens/HelpCenterScreen.jsx';
import AboutScreen from './screens/AboutScreen.jsx';
import LoginScreen from './screens/LoginScreen.jsx';
import RegisterScreen from './screens/RegisterScreen.jsx';
import AppOnboardingScreen from './screens/AppOnboardingScreen.jsx';
import LandingPage from './screens/LandingPage.jsx';

// Layout component for authenticated routes with header
const AuthenticatedLayout = ({ children }) => {
  const { theme, isDarkMode } = useTheme();
  
  return (
    <View style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.background,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {children}
    </View>
  );
};

// Protected Route wrapper with onboarding check
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user || !requireOnboarding) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        // Import here to avoid circular dependencies
        const { getAuth } = await import('firebase/auth');
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const { initializeApp, getApp } = await import('firebase/app');
        const { firebaseConfig } = await import('../firebase.config');
        
        let app;
        try {
          app = getApp();
        } catch {
          app = initializeApp(firebaseConfig);
        }
        const db = getFirestore(app);
        const auth = getAuth(app);
        
        if (auth.currentUser) {
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsOnboarded(userData.isOnboarded === true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user, requireOnboarding]);

  if (loading || (requireOnboarding && checkingOnboarding)) {
    return (
      <View style={{ 
        flex: 1, 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#0F172A' 
      }}>
        <ActivityIndicator size="large" color="#60A5FA" />
        <Text style={{ color: '#F8FAFC', marginTop: 16, fontSize: 16 }}>Loading...</Text>
      </View>
    );
  }

  // Check if in demo mode (no authentication required)
  // FOR DEMO: Always enable demo mode
  if (typeof window !== 'undefined') {
    localStorage.setItem('isDemoMode', 'true');
  }
  const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
  
  if (!user && !isDemoMode) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Skip onboarding for demo - always go to home
  // Redirect to onboarding if not completed (only for routes that require onboarding) - skip for demo mode
  if (user && requireOnboarding && !isOnboarded && location.pathname !== '/onboarding' && !isDemoMode) {
    return <Navigate to="/onboarding" replace />;
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

// Public Route wrapper (redirects to home or onboarding if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        // Import here to avoid circular dependencies
        const { getAuth } = await import('firebase/auth');
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const { initializeApp, getApp } = await import('firebase/app');
        const { firebaseConfig } = await import('../firebase.config');
        
        let app;
        try {
          app = getApp();
        } catch {
          app = initializeApp(firebaseConfig);
        }
        const db = getFirestore(app);
        const auth = getAuth(app);
        
        if (auth.currentUser) {
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsOnboarded(userData.isOnboarded === true);
          } else {
            // New user, not onboarded
            setIsOnboarded(false);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
        setIsOnboarded(false);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  if (loading || (user && checkingOnboarding)) {
    return (
      <View style={{ 
        flex: 1, 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#0F172A' 
      }}>
        <ActivityIndicator size="large" color="#60A5FA" />
        <Text style={{ color: '#F8FAFC', marginTop: 16, fontSize: 16 }}>Loading...</Text>
      </View>
    );
  }

  if (user) {
    // Skip onboarding for demo - always go to home
    const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
    if (isDemoMode) {
      return <Navigate to="/home" replace />;
    }
    // Redirect to onboarding if not completed, otherwise home
    return <Navigate to={isOnboarded ? "/home" : "/onboarding"} replace />;
  }

  return children;
};

// Main App Component with Routing
const AppRoutes = () => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingReferrerId, setPendingReferrerId] = useState(null);

  // Handle deep linking for referral links
  useEffect(() => {
    const handleDeepLink = (url) => {
      try {
        const referrerId = referralService.extractReferrerFromUrl(url);
        if (referrerId && !user) {
          setPendingReferrerId(referrerId);
        }
      } catch (error) {
        console.error('Error handling deep link:', error);
      }
    };

    // Handle initial URL
    Linking.getInitialURL().then((url) => {
      if (url) {
        handleDeepLink(url);
      }
    });

    // Handle URL changes
    const subscription = Linking.addEventListener('url', ({ url }) => {
      handleDeepLink(url);
    });

    return () => {
      subscription.remove();
    };
  }, [user]);

  // Navigate to Register screen with referrer ID when available
  useEffect(() => {
    if (pendingReferrerId && !user) {
      navigate('/register', { state: { referrerId: pendingReferrerId } });
      setPendingReferrerId(null);
    }
  }, [pendingReferrerId, user, navigate]);

  if (loading) {
    return (
      <View style={{ 
        flex: 1, 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: theme.background 
      }}>
        <Text style={{ color: theme.text, fontSize: 18, marginTop: 16 }}>Loading...</Text>
      </View>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={<LandingPage />} 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginScreen />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterScreen />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute requireOnboarding={false}>
            <AppOnboardingScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <HomeScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfileScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/class/:classId" 
        element={
          <ProtectedRoute>
            <ClassDetailScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/set/:setId" 
        element={
          <ProtectedRoute>
            <SetDetailScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-set" 
        element={
          <ProtectedRoute>
            <CreateSetScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/edit-set/:setId" 
        element={
          <ProtectedRoute>
            <EditSetScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-flashcard/:setId" 
        element={
          <ProtectedRoute>
            <CreateFlashcardScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/edit-flashcard/:cardId" 
        element={
          <ProtectedRoute>
            <EditFlashcardScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/play/:setId" 
        element={
          <ProtectedRoute>
            <PlayScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-generate" 
        element={
          <ProtectedRoute>
            <AIGenerateScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/document-to-flashcards" 
        element={
          <ProtectedRoute>
            <DocumentToFlashcardsScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/privacy-policy" 
        element={
          <ProtectedRoute>
            <PrivacyPolicyScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/terms-of-service" 
        element={
          <ProtectedRoute>
            <TermsOfServiceScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help-center" 
        element={
          <ProtectedRoute>
            <HelpCenterScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/about" 
        element={
          <ProtectedRoute>
            <AboutScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsScreen />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pricing" 
        element={
          <ProtectedRoute>
            <PricingScreen />
          </ProtectedRoute>
        } 
      />

      {/* Default redirect for authenticated users */}
      <Route 
        path="/home-redirect" 
        element={
          user ? <Navigate to="/home" replace /> : <Navigate to="/" replace />
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <Router>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <FlashcardProvider>
              <AppRoutes />
              <Analytics />
            </FlashcardProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </Router>
  );
}

// Fallback if app fails to load
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    console.error('Global error:', e.error);
  });
  
  window.addEventListener('unhandledrejection', (e) => {
    console.error('Unhandled promise rejection:', e.reason);
  });
}

